import { Badge } from '../ui/badge';

// Define all possible project statuses with their standardized styling
export const PROJECT_STATUS_STYLES = {
  'project_initiated': { bg: '!bg-purple-400', text: '!text-purple-900', border: '!border-purple-300', label: 'Project Initiated' },
  'site_assessment': { bg: '!bg-indigo-400', text: '!text-indigo-900', border: '!border-indigo-300', label: 'Site Assessment' },
  'permits_approvals': { bg: '!bg-orange-400', text: '!text-orange-900', border: '!border-orange-300', label: 'Permits & Approvals' },
  'materials_ordered': { bg: '!bg-teal-400', text: '!text-teal-900', border: '!border-teal-300', label: 'Materials Ordered' },
  'installation_begins': { bg: '!bg-blue-400', text: '!text-blue-900', border: '!border-blue-300', label: 'Installation Begins' },
  'quality_inspection': { bg: '!bg-green-400', text: '!text-green-900', border: '!border-green-300', label: 'Quality Inspection' },
  'completed': { bg: '!bg-emerald-400', text: '!text-emerald-900', border: '!border-emerald-300', label: 'Completed' },
  'pending': { bg: '!bg-indigo-400', text: '!text-indigo-900', border: '!border-indigo-300', label: 'Pending' },
  'in_progress': { bg: '!bg-cyan-400', text: '!text-cyan-900', border: '!border-cyan-300', label: 'In Progress' },
  'on_hold': { bg: '!bg-green-400', text: '!text-green-900', border: '!border-green-300', label: 'On Hold' },
  'cancelled': { bg: '!bg-rose-400', text: '!text-rose-900', border: '!border-rose-300', label: 'Cancelled' }
} as const;

// Define estimate-specific statuses
export const ESTIMATE_STATUS_STYLES = {
  'draft': { bg: '!bg-stone-400', text: '!text-stone-900', border: '!border-stone-300', label: 'Draft' },
  'sent': { bg: '!bg-blue-400', text: '!text-blue-900', border: '!border-blue-300', label: 'Sent' },
  'accepted': { bg: '!bg-green-400', text: '!text-green-900', border: '!border-green-300', label: 'Accepted' },
  'rejected': { bg: '!bg-red-400', text: '!text-red-900', border: '!border-red-300', label: 'Rejected' },
  'expired': { bg: '!bg-green-400', text: '!text-green-900', border: '!border-green-300', label: 'Expired' },
  'converted': { bg: '!bg-purple-400', text: '!text-purple-900', border: '!border-purple-300', label: 'Converted' }
} as const;

// Define invoice-specific statuses
export const INVOICE_STATUS_STYLES = {
  'paid': { bg: '!bg-green-600', text: '!text-white', border: '!border-green-500', label: 'Paid' },
  'partially_paid': { bg: '!bg-green-600', text: '!text-white', border: '!border-green-500', label: 'Partial Paid' },
  'pending': { bg: '!bg-orange-500', text: '!text-white', border: '!border-orange-400', label: 'Pending' },
  'overdue': { bg: '!bg-red-500', text: '!text-white', border: '!border-red-400', label: 'Overdue' },
  'draft': { bg: '!bg-slate-500', text: '!text-white', border: '!border-slate-400', label: 'Draft' },
  'cancelled': { bg: '!bg-slate-500', text: '!text-white', border: '!border-slate-400', label: 'Cancelled' }
} as const;

interface StatusBadgeProps {
  status: string;
  type?: 'project' | 'estimate' | 'invoice';
}

export default function StatusBadge({ status, type = 'project' }: StatusBadgeProps) {
  if (!status) {
    return <Badge variant="outline" className="!bg-gray-400 !text-gray-900 !border-gray-300 font-semibold">Unknown</Badge>;
  }

  let style;
  
  if (type === 'project') {
    style = PROJECT_STATUS_STYLES[status as keyof typeof PROJECT_STATUS_STYLES];
  } else if (type === 'estimate') {
    style = ESTIMATE_STATUS_STYLES[status as keyof typeof ESTIMATE_STATUS_STYLES];
  } else if (type === 'invoice') {
    style = INVOICE_STATUS_STYLES[status as keyof typeof INVOICE_STATUS_STYLES];
  }

  if (style) {
    return (
      <Badge 
        variant="outline" 
        className={`${style.bg} ${style.text} ${style.border} font-semibold`}
      >
        {style.label}
      </Badge>
    );
  }

  // Fallback for unknown statuses
  return (
    <Badge variant="outline" className="!bg-gray-400 !text-gray-900 !border-gray-300 font-semibold">
      {status}
    </Badge>
  );
}

// Helper function for backward compatibility
export function getStandardStatusBadge(status: string, type: 'project' | 'estimate' | 'invoice' = 'project') {
  return <StatusBadge status={status} type={type} />;
}
